/**
 * Warehouse Reconciliation Tab
 * Tab 2 of Banker Recon - handles warehouse dates, fees, credits, and document upload
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Upload,
  FileText,
  Calendar,
  Eye,
  Trash2,
  Loader2,
  CircleDot,
  Save,
  Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/utils/supabase';
import { reserveLoanStorageObject } from '@/services/loanStorageAuthorizationService';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { effectiveWarehouseDays } from '@/utils/banker-recon/warehouseDays';
import { buildReconDocName } from '@/utils/banker-recon/reconDocName';
import type { EncompassLoan } from '@/components/broker-recon/LoanAutocomplete';
import {
  type KeyDates,
  type CostsState,
  type UploadedDocument,
  formatCurrency as formatCurrencyFn,
  formatNumberWithCommas as formatNumberWithCommasFn,
  parseFormattedNumber as parseFormattedNumberFn
} from './types';

// ============================================================================
// PROPS
// ============================================================================

interface WarehouseReconTabProps {
  selectedLoan: EncompassLoan | null;
  user: { id: string; organization_id: string } | null;

  // Key dates
  keyDates: KeyDates;
  setKeyDates: React.Dispatch<React.SetStateAction<KeyDates>>;

  // Costs
  costs: CostsState;
  setCosts: React.Dispatch<React.SetStateAction<CostsState>>;

  // Documents (filtered to warehouse type)
  uploadedDocuments: UploadedDocument[];
  setUploadedDocuments: React.Dispatch<React.SetStateAction<UploadedDocument[]>>;

  // Calculated values
  totalWarehouseCosts: number;

  // Dirty state callback
  setIsDirty: (dirty: boolean) => void;

  // Save functionality
  onSaveDraft: () => Promise<unknown>;
  isSaving: boolean;
  isDirty: boolean;
  saveSuccess: boolean;

  // Auto-fill: notify the parent coordinator when a doc finishes extracting
  onDocumentReady?: (documentId: string) => void;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({ value, onChange, className, placeholder, prefix, suffix }) => {
  const [displayValue, setDisplayValue] = useState(value ? formatNumberWithCommasFn(value) : '');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setDisplayValue(value ? formatNumberWithCommasFn(value) : '');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const sanitized = rawValue.replace(/[^0-9.,-]/g, '');
    setDisplayValue(sanitized);
    onChange(parseFormattedNumberFn(sanitized));
  };

  const handleBlur = () => {
    if (displayValue) {
      const num = parseFormattedNumberFn(displayValue);
      setDisplayValue(num ? formatNumberWithCommasFn(num) : '');
    }
  };

  if (prefix || suffix) {
    return (
      <div className={cn("relative flex items-center", className)}>
        {prefix && (
          <span className="absolute left-3 text-gray-500 pointer-events-none">{prefix}</span>
        )}
        <Input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          className={cn(
            "w-full",
            prefix && "pl-7",
            suffix && "pr-12"
          )}
          placeholder={placeholder}
        />
        {suffix && (
          <span className="absolute right-3 text-gray-500 pointer-events-none">{suffix}</span>
        )}
      </div>
    );
  }

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      placeholder={placeholder}
    />
  );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const parseDateString = (dateStr: string | null): Date | null => {
  if (!dateStr) {
return null;
}
  const date = parseAsLocalDate(dateStr);
  return isNaN(date.getTime()) ? null : date;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) {
return 'N/A';
}
  const date = parseDateString(dateStr);
  if (!date) {
return 'N/A';
}
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

// `calculateDaysBetween` + `effectiveWarehouseDays` (dates-win) now live in the shared util so the save path
// (BankerRecon) and this display agree — imported at the top of the file.

// ============================================================================
// COMPONENT
// ============================================================================

export const WarehouseReconTab: React.FC<WarehouseReconTabProps> = ({
  selectedLoan,
  user,
  keyDates,
  setKeyDates,
  costs,
  setCosts,
  uploadedDocuments,
  setUploadedDocuments,
  totalWarehouseCosts,
  setIsDirty,
  onSaveDraft,
  isSaving,
  isDirty,
  saveSuccess,
  onDocumentReady
}) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter documents to only warehouse reconciliation type
  const warehouseDocuments = uploadedDocuments.filter(
    doc => doc.documentType === 'warehouse_reconciliation'
  );

  // ============================================================================
  // COST UPDATE HANDLERS
  // ============================================================================

  const updateWarehouseCost = useCallback((id: string, amount: number) => {
    setCosts(prev => ({
      ...prev,
      warehouse: prev.warehouse.map(item =>
        item.id === id ? { ...item, amount } : item
      )
    }));
    setIsDirty(true);
  }, [setCosts, setIsDirty]);

  // ============================================================================
  // DOCUMENT UPLOAD HANDLERS
  // ============================================================================

  const processFileUpload = useCallback(async (file: File) => {
    if (!user || !selectedLoan) {
      return;
    }

    setUploading(true);

    const documentId = `doc-${Date.now()}`;
    // Human-readable name: "{loan#} - {LastName} - Warehouse Recon.pdf" (falls back to the raw upload name).
    const docName = buildReconDocName(selectedLoan, 'Warehouse Recon', file.name);
    // The documentId sub-folder keeps the storage key unique across re-uploads while the basename is readable.
    const storagePath = `${user.organization_id}/${selectedLoan.encompass_loan_id}/${documentId}/${docName}`;

    try {
      await reserveLoanStorageObject(
        storagePath,
        selectedLoan.encompass_loan_id
      );

      // Upload to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('loan-documents')
        .upload(storagePath, file);

      if (uploadError) {
        throw uploadError;
      }

      // Insert into loan_documents immediately so it's tracked. title + original_filename drive every in-app
      // display surface; the raw upload name is preserved in metadata for audit.
      const { data: docRecord, error: docError } = await supabase
        .from('loan_documents')
        .insert({
          organization_id: user.organization_id,
          encompass_loan_id: selectedLoan.encompass_loan_id,
          source_file: uploadData.path,
          original_filename: docName,
          title: docName,
          metadata: { uploaded_filename: file.name },
          document_type: 'warehouse_reconciliation',
          uploaded_by: user.id,
          status: 'processing'
        })
        .select('id')
        .single();

      if (docError) {
        throw docError;
      }

      // Queue for GPU extraction (Docling)
      const { error: queueError } = await supabase
        .from('document_processing_queue')
        .insert({
          organization_id: user.organization_id,
          encompass_loan_id: selectedLoan.encompass_loan_id,
          storage_path: uploadData.path,
          status: 'pending',
          source_type: 'banker-recon',
          user_id: user.id,
          document_id: docRecord.id
        });

      if (queueError) {
        throw queueError;
      }

      // Add to uploaded documents with warehouse_reconciliation type
      const newDoc: UploadedDocument = {
        id: docRecord.id,  // Use the database ID
        file,
        fileName: docName,
        documentType: 'warehouse_reconciliation',
        confidence: 1,
        storagePath: uploadData.path,
        uploadedAt: new Date(),
        processingStatus: 'queued',
        progress: 0
      };

      setUploadedDocuments(prev => [...prev, newDoc]);
      setIsDirty(true);  // Enable Save button after upload

      // Subscribe to queue updates for Docling extraction progress
      const channel = supabase
        .channel(`warehouse-doc-${documentId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'document_processing_queue',
            // Filter by document_id (set on the queue row below) — independent of the readable storage path.
            filter: `document_id=eq.${docRecord.id}`
          },
          (payload) => {
            const queueItem = payload.new as {
              status: string;
              extracted_text: string | null;
              error_message: string | null;
              progress: number;
            };

            if (queueItem.status === 'extracted' || queueItem.status === 'completed') {
              // Docling extraction complete - mark document as ready
              setUploadedDocuments(prev =>
                prev.map(doc =>
                  doc.id === docRecord.id
                    ? { ...doc, processingStatus: 'complete' as const }
                    : doc
                )
              );
              toast({
                title: 'Document processed',
                description: 'Document text extracted and attached to record'
              });
              onDocumentReady?.(docRecord.id); // hand off to the parent auto-fill coordinator
              channel.unsubscribe();
            } else if (queueItem.status === 'failed') {
              setUploadedDocuments(prev =>
                prev.map(doc =>
                  doc.id === docRecord.id
                    ? { ...doc, processingStatus: 'failed' as const }
                    : doc
                )
              );
              toast({
                variant: 'destructive',
                title: 'Processing failed',
                description: queueItem.error_message || 'Document extraction failed'
              });
              channel.unsubscribe();
            } else {
              // Update progress
              setUploadedDocuments(prev =>
                prev.map(doc =>
                  doc.id === docRecord.id
                    ? { ...doc, progress: Math.round(queueItem.progress || 0), processingStatus: 'extracting' as const }
                    : doc
                )
              );
            }
          }
        )
        .subscribe();

      toast({
        title: 'Document uploaded',
        description: 'Extracting text from document...'
      });

    } catch (err) {
      console.error('Upload failed:', err);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: 'Could not upload document'
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [user, selectedLoan, setUploadedDocuments, setIsDirty, toast, onDocumentReady]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (selectedLoan) {
      setIsDragging(true);
    }
  }, [selectedLoan]);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (!selectedLoan) {
      return;
    }

    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].type === 'application/pdf') {
      processFileUpload(files[0]);
    } else {
      toast({
        variant: 'destructive',
        title: 'Invalid file',
        description: 'Please upload a PDF file'
      });
    }
  }, [selectedLoan, toast, processFileUpload]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFileUpload(file);
    }
  }, [processFileUpload]);

  const handleViewDocument = useCallback(async (storagePath: string) => {
    const { data } = await supabase.storage
      .from('loan-documents')
      .createSignedUrl(storagePath, 3600);

    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    } else {
      toast({
        variant: 'destructive',
        title: 'Could not open document',
        description: 'Failed to generate signed URL'
      });
    }
  }, [toast]);

  const handleRemoveDocument = useCallback(async (documentId: string) => {
    const doc = uploadedDocuments.find(d => d.id === documentId);

    // Delete from storage
    if (doc?.storagePath) {
      await supabase.storage
        .from('loan-documents')
        .remove([doc.storagePath]);
    }

    // Delete from loan_documents table
    await supabase
      .from('loan_documents')
      .delete()
      .eq('id', documentId);

    setUploadedDocuments(prev => prev.filter(d => d.id !== documentId));
    setIsDirty(true);
    toast({ title: 'Document removed' });
  }, [uploadedDocuments, setUploadedDocuments, setIsDirty, toast]);

  // ============================================================================
  // RENDER
  // ============================================================================

  // Save Draft button component for reuse
  const SaveDraftButton = () => (
    <Button
      onClick={onSaveDraft}
      disabled={isSaving || saveSuccess || !selectedLoan || !isDirty}
      variant={saveSuccess ? "outline" : isDirty ? "default" : "outline"}
      size="sm"
      className={saveSuccess ? "text-green-600 border-green-600" : ""}
    >
      {isSaving ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : saveSuccess ? (
        <Check className="h-4 w-4 mr-2" />
      ) : (
        <Save className="h-4 w-4 mr-2" />
      )}
      {isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Draft'}
    </Button>
  );

  return (
    <div className="space-y-6">
      {/* Top Save Button */}
      <div className="flex justify-end">
        <SaveDraftButton />
      </div>

      {/* Document Upload Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-gray-500" />
            Warehouse Reconciliation Document
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Upload Zone */}
            <div
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
                isDragging
                  ? "border-blue-500 bg-blue-50"
                  : !selectedLoan
                    ? "border-gray-200 opacity-50"
                    : "border-gray-300 hover:border-blue-400"
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf"
                onChange={handleFileChange}
                disabled={uploading || !selectedLoan}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !selectedLoan}
                className={cn(
                  "w-full flex flex-col items-center gap-2",
                  (!selectedLoan || uploading) && "opacity-50 cursor-not-allowed"
                )}
              >
                <Upload className="h-8 w-8 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                </span>
                <span className="text-xs text-gray-400">PDF files only</span>
              </button>
            </div>

            {/* Processing Progress */}
            {warehouseDocuments.some(d => d.processingStatus && d.processingStatus !== 'complete' && d.processingStatus !== 'failed') && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  <span className="text-sm text-blue-700">
                    Extracting text from document...
                  </span>
                </div>
                <Progress
                  value={100}
                  className="h-2 mt-2 [&>div]:animate-pulse"
                />
              </div>
            )}

            {/* Uploaded Documents */}
            {warehouseDocuments.length > 0 && (
              <div className="space-y-2">
                {warehouseDocuments.map(doc => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium">{doc.fileName || doc.file?.name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {doc.processingStatus === 'complete' ? (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              Processed
                            </Badge>
                          ) : doc.processingStatus === 'failed' ? (
                            <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                              Failed
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">
                              Processing...
                            </Badge>
                          )}
                          <span className="text-gray-400">•</span>
                          <span>{doc.uploadedAt.toLocaleDateString()} {doc.uploadedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.processingStatus === 'complete' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewDocument(doc.storagePath)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveDocument(doc.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Key Dates Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-gray-500" />
            Key Dates
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Row 1: Closing Date, Days on Warehouse, Daily Interest Cost, Annual Interest Rate (read-only/calculated) */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div>
              <Label className="text-xs text-gray-500">Disbursement Date (2553)</Label>
              <p className="font-medium text-sm mt-1.5 h-9 flex items-center">
                {selectedLoan?.closing_date
                  ? formatDate(selectedLoan.closing_date)
                  : '—'}
              </p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Days on Warehouse</Label>
              <p className="font-medium text-lg mt-1.5 h-9 flex items-center">
                {effectiveWarehouseDays(keyDates) > 0
                  ? `${effectiveWarehouseDays(keyDates)} days`
                  : '—'}
              </p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Daily Interest Cost</Label>
              <p className="font-medium text-sm mt-1.5 h-9 flex items-center">
                {(() => {
                  const warehouseInterest = costs.warehouse.find(w => w.id === 'warehouse_interest')?.amount || 0;
                  const days = effectiveWarehouseDays(keyDates);
                  if (days > 0 && warehouseInterest > 0) {
                    return `${formatCurrencyFn(warehouseInterest / days)}/day`;
                  }
                  return '—';
                })()}
              </p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Annual Interest Rate</Label>
              <p className="font-medium text-sm mt-1.5 h-9 flex items-center">
                {(() => {
                  const warehouseInterest = costs.warehouse.find(w => w.id === 'warehouse_interest')?.amount || 0;
                  const days = effectiveWarehouseDays(keyDates);
                  const loanAmount = selectedLoan?.trans_details_loan_amt || 0;
                  if (days > 0 && warehouseInterest > 0 && loanAmount > 0) {
                    const dailyCost = warehouseInterest / days;
                    const annualRate = (dailyCost * 365) / loanAmount * 100;
                    return `${annualRate.toFixed(3)}%`;
                  }
                  return '—';
                })()}
              </p>
            </div>
          </div>

          {/* Row 2: Funds to Escrow, Funds Received, Warehouse Interest, Non-QM Haircut */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label htmlFor="fundsToEscrow" className="text-xs text-gray-500">Funds to Escrow</Label>
              <Input
                id="fundsToEscrow"
                type="date"
                value={keyDates.fundsToEscrow}
                onChange={(e) => {
                  setKeyDates(prev => ({ ...prev, fundsToEscrow: e.target.value }));
                  setIsDirty(true);
                }}
              />
            </div>
            <div>
              <Label htmlFor="fundsReceived" className="text-xs text-gray-500">Funds Received</Label>
              <Input
                id="fundsReceived"
                type="date"
                value={keyDates.fundsReceived}
                onChange={(e) => {
                  setKeyDates(prev => ({ ...prev, fundsReceived: e.target.value }));
                  setIsDirty(true);
                }}
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Warehouse Interest</Label>
              <CurrencyInput
                value={costs.warehouse.find(w => w.id === 'warehouse_interest')?.amount || 0}
                onChange={(val) => {
                  setCosts(prev => ({
                    ...prev,
                    warehouse: prev.warehouse.map(w =>
                      w.id === 'warehouse_interest' ? { ...w, amount: val } : w
                    )
                  }));
                  setIsDirty(true);
                }}
                className="h-9"
                placeholder="0.00"
                prefix="$"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Non-QM Haircut</Label>
              <Select
                value={String(costs.warehouseHaircutPercent || 0)}
                onValueChange={(val) => {
                  setCosts(prev => ({ ...prev, warehouseHaircutPercent: Number(val) }));
                  setIsDirty(true);
                }}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">None (0%)</SelectItem>
                  <SelectItem value="2">Non-QM (2%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Timeline Visual */}
          {keyDates.fundsToEscrow && keyDates.fundsReceived && (
            <div className="mt-4 pt-4 border-t space-y-2">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{selectedLoan?.closing_date ? formatDate(selectedLoan.closing_date) : ''}</span>
                <span>{formatDate(keyDates.fundsToEscrow)}</span>
                <span>{formatDate(keyDates.fundsReceived)}</span>
              </div>
              <div className="flex items-center gap-2">
                <CircleDot className="h-3 w-3 text-gray-400" />
                <Progress value={100} className="flex-1 h-2 bg-amber-100" />
                <CircleDot className="h-3 w-3 text-green-500" />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Closing</span>
                <span className="font-medium text-amber-600">
                  {effectiveWarehouseDays(keyDates)} days on warehouse
                </span>
                <span className="text-gray-500">Funds Received</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Warehouse Lender Fees */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Warehouse Lender Fees</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 rounded-lg p-4 space-y-1">
            {/* Warehouse Interest is shown in Key Dates section above */}
            {costs.warehouse.filter(item => item.id !== 'warehouse_interest').map(item => (
              <div key={item.id} className="flex items-center justify-between py-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">├─</span>
                  <span className="text-sm text-gray-600">{item.description}</span>
                </div>
                <CurrencyInput
                  value={item.amount}
                  onChange={(val) => updateWarehouseCost(item.id, val)}
                  className="w-32 text-right"
                  placeholder="0.00"
                />
              </div>
            ))}
            <Separator className="my-2" />
            <div className="flex items-center justify-between py-2 font-semibold border-t">
              <span className="text-gray-700">Total Warehouse Fees</span>
              <span className="font-mono text-red-600">
                {formatCurrencyFn(-totalWarehouseCosts)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warehouse Lender Credits */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Warehouse Lender Credits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-green-50 rounded-lg p-4 space-y-1">
            <div className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2">
                <span className="text-gray-400">├─</span>
                <span className="text-sm text-gray-600">FlexClose Credit</span>
              </div>
              <CurrencyInput
                value={costs.flexcloseCredit}
                onChange={(val) => {
                  setCosts(prev => ({ ...prev, flexcloseCredit: Math.max(0, val) }));
                  setIsDirty(true);
                }}
                className="w-32 text-right"
                placeholder="0.00"
              />
            </div>
            <Separator className="my-2" />
            <div className="flex items-center justify-between py-2 font-semibold border-t">
              <span className="text-gray-700">Total Warehouse Credits</span>
              <span className="font-mono text-green-600">
                {formatCurrencyFn(costs.flexcloseCredit)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bottom Save Button */}
      <div className="flex justify-end">
        <SaveDraftButton />
      </div>
    </div>
  );
};
